/**
 * AvatarOnboarding - Post-registration avatar customization
 * 
 * Shown after new user registration to let them customize their avatar
 * before continuing to the dashboard.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import AvatarEditor from '@/components/AvatarEditor';
import RadioTower from '@/components/RadioTower';
import Baseball from '@/components/Baseball';
import axios from 'axios';
import { toast } from 'sonner';
import { playSuccess } from '@/utils/sounds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AvatarOnboarding() {
  const navigate = useNavigate();
  const { getAuthHeader, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSave = async (config) => {
    setLoading(true);
    try {
      // Update avatar
      await axios.put(`${API}/profile/avatar`, {
        gender: config.gender,
        hair_id: config.hair_id,
        primary_color: config.primary_color,
        secondary_color: config.secondary_color,
        equipped_flair: config.equipped_flair,
      }, { headers: getAuthHeader() });

      // Mark setup as complete
      await axios.post(`${API}/profile/avatar/complete-setup`, {}, { 
        headers: getAuthHeader() 
      });

      // Refresh user data
      await refreshUser();

      playSuccess();
      toast.success('Looking good! Welcome to Radio Baseball!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save avatar:', error);
      toast.error(error.response?.data?.detail || 'Failed to save avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      // Mark setup as complete without changes
      await axios.post(`${API}/profile/avatar/complete-setup`, {}, { 
        headers: getAuthHeader() 
      });
      
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to skip:', error);
      navigate('/dashboard'); // Still navigate even on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-4 mb-4">
            <RadioTower size={50} animated={true} />
            <Baseball size={60} />
          </div>
          <h1 className="retro-heading text-xl text-ink mb-2">
            CREATE YOUR PLAYER
          </h1>
          <p className="retro-body text-lg text-dirt">
            Customize your baseball avatar
          </p>
        </div>

        {/* Avatar Editor */}
        <AvatarEditor
          onSave={handleSave}
          showCancel={false}
          saveLabel="LET'S PLAY!"
        />

        {/* Skip Option */}
        <div className="mt-4 text-center">
          <button
            onClick={handleSkip}
            disabled={loading}
            className="retro-text text-sm text-dirt hover:text-ink underline"
            data-testid="skip-avatar-btn"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}
